import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Table } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddDataConsumersToOrganization,
  useDataConsumersPublic,
} from "../../../api/api.data-consumers";
import { DataConsumerBase } from "../../../api/models";
import { toAntdPagination } from "../../../util";

interface AddDataConsumerButtonProps {
  organizationId: string;
  currentDataConsumers: DataConsumerBase[];
  refresh: () => void;
}

export function AddDataConsumerButton({
  organizationId,
  currentDataConsumers,
  refresh,
}: AddDataConsumerButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const addDataConsumersToOrg =
    useAddDataConsumersToOrganization(organizationId);

  const error = () => {
    Modal.error({
      title: t("error"),
      content: t("error-save"),
    });
  };

  const onCancel = () => {
    setIsOpen(false);
    setSelectedIds([]);
  };

  const saveDataConsumer = async () => {
    setIsSaving(true);
    const result = await addDataConsumersToOrg({ consumerIds: selectedIds });
    setIsSaving(false);

    if (result.error) error();
    else {
      setIsOpen(false);
      setSelectedIds([]);
      refresh();
    }
  };

  const modalFooter = [
    <Button key="cancel" onClick={onCancel}>
      {t("cancel")}
    </Button>,
    <Button
      key="add"
      type="primary"
      loading={isSaving}
      disabled={selectedIds.length === 0}
      onClick={saveDataConsumer}
      data-testid="add-data-consumer-modal-add-button"
    >
      {t("integrations:action-data-consumer", {
        action: t("translations:add"),
      })}
    </Button>,
  ];

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        type="primary"
        data-testid="add-data-consumer-button"
      >
        <PlusCircleOutlined />
        {t("integrations:action-data-consumer", {
          action: t("translations:add"),
        })}
      </Button>
      <Modal
        title={t("integrations:action-data-consumer", {
          action: t("translations:add"),
        })}
        width="50%"
        open={isOpen}
        onCancel={onCancel}
        footer={modalFooter}
        data-testid="add-data-consumer-modal"
      >
        <PublicDataConsumersTable
          currentDataConsumers={currentDataConsumers}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
        />
      </Modal>
    </>
  );
}

interface PublicDataConsumersTableProps {
  currentDataConsumers: DataConsumerBase[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}
const PublicDataConsumersTable = ({
  currentDataConsumers,
  selectedIds,
  setSelectedIds,
}: PublicDataConsumersTableProps) => {
  const { t } = useTranslation();

  const dataConsumersPublic = useDataConsumersPublic();

  // Filter out data consumers that are already enabled to the organization
  const filteredItems = (dataConsumersPublic.value?.items || []).filter(
    (dc) => !currentDataConsumers.some((cdc) => cdc.id === dc.id)
  );

  const sortedFilteredItems = filteredItems.sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  return (
    <div data-testid="add-data-consumers-table">
      <Table
        sticky
        size="middle"
        scroll={{ y: "calc(100vh - 200px)" }}
        dataSource={sortedFilteredItems || []}
        rowSelection={{
          type: "checkbox",
          onChange: (selectedRows) => {
            setSelectedIds(selectedRows as string[]);
          },
          selectedRowKeys: selectedIds,
        }}
        loading={dataConsumersPublic.loading}
        rowKey="id"
        columns={[
          {
            title: t("integrations:data-consumer-name"),
            key: "name",
            render: (c: DataConsumerBase) => c.name,
          },
        ]}
        pagination={toAntdPagination(dataConsumersPublic, false)}
      />
    </div>
  );
};
